/**
 * Noibu Feature Flag: sticky Check out bar on the mobile cart
 * (flag "2083-sticky-check-out-bar-on-mobile-cart", Noibu A/B test 134).
 *
 * Structurally parallel to assets/noibu-flag-search.js and
 * assets/noibu-flag-cart.js. It is a separate file on purpose: those files
 * define the controls for running experiments and must not change mid-flight.
 *
 * The bar is server-rendered by sections/noibu-sticky-checkout-ab.liquid and
 * is hidden until this script says otherwise. A flag failure is therefore a
 * no-op and the cart renders exactly as it does today.
 *
 *   original              -> html.nff-sticky--a  (control, the default)
 *   sticky-check-out-bar  -> html.nff-sticky--b
 *
 * In variation B the bar:
 *   - mirrors the cart footer's estimated total (kept in sync when Dawn
 *     re-renders the footer after a quantity change or a removal),
 *   - submits the existing cart form with name="checkout", exactly like the
 *     footer's own Check out button,
 *   - slides away while the real Check out button is on screen, so the page
 *     never shows two Check out buttons at once,
 *   - hides itself when the cart is empty.
 *
 * Targeting (device type is mobile, phones and tablets) lives in the Noibu
 * test. There is deliberately no CSS breakpoint: a landscape tablet is wider
 * than Dawn's 750px breakpoint, and gating by width would enrol it into B
 * while it still saw the control.
 *
 * Noibu's implementation code for test 134 (console > A/B tests >
 * Implementation), pasted verbatim:
 *
 *   function renderStickyCheckOutBar(container) {
 *     // TODO: the implementation for variation B
 *   }
 *
 *   function renderOriginal(container) {
 *     // TODO: the implementation for the default experience
 *   }
 *
 *   function render(container) {
 *     const { variation } = client.getStringDetails(
 *       "2083-sticky-check-out-bar-on-mobile-cart",
 *       "original"
 *     );
 *
 *     switch (variation) {
 *       case "sticky-check-out-bar":
 *         // Variation B
 *         return renderStickyCheckOutBar(container);
 *       case "original":
 *         // Variation A (control)
 *         // Since this is the original experience, add no code here and
 *         // let this fall through to the default.
 *       default:
 *         // This should always be the original experience. Users excluded
 *         // by targeting will see this.
 *         return renderOriginal(container);
 *     }
 *   }
 *
 *   // `client` is the ready client from the Noibu A/B testing SDK.
 *   // Call render(container) once your feature flag client is ready.
 *
 * SDK NOTE: the snippet destructures `variation`, but the details object the
 * SDK returns carries the arm as `variant` (and `value`), e.g.
 * { variant: 'show-reassurance', value: 'show-reassurance', reason: 'CACHED' }
 * for 2083-cart-reassurance on this store. Copied verbatim, every visitor
 * would fall through to the control, so readVariation() reads all three.
 */
(function () {
  'use strict';

  var FLAG = '2083-sticky-check-out-bar-on-mobile-cart';
  var CONTROL = 'original';
  var VARIATION_B = 'sticky-check-out-bar';
  var CACHE_KEY = 'nff.sticky-checkout';

  var REPORT_KEY = 'nff_sticky-checkout';
  var STATE_ATTR = 'data-nff-sticky-state';

  // Anchored to window.load because the client cannot arrive before it:
  // collect-core is injected by a ScriptTag on load, and collect-ff by
  // collect-core after that.
  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-sticky--a';
  CLASSES[VARIATION_B] = 'nff-sticky--b';

  var root = document.documentElement;

  /* ------------------------------------------------------------------ */
  /* Variation B behaviour                                               */
  /* ------------------------------------------------------------------ */

  var enhanced = false;

  function bar() {
    return document.querySelector('[data-nff-sticky-checkout]');
  }

  function syncTotal() {
    var el = bar();
    if (!el) return;
    var source = document.querySelector('#main-cart-footer .totals__total-value');
    var target = el.querySelector('[data-nff-sticky-total]');
    if (source && target && target.textContent !== source.textContent) {
      target.textContent = source.textContent;
    }
  }

  function syncEmpty() {
    var el = bar();
    if (!el) return;
    var footer = document.getElementById('main-cart-footer');
    var empty = !footer || footer.classList.contains('is-empty');
    el.classList.toggle('is-empty', empty);
    var button = el.querySelector('button[name="checkout"]');
    if (button) button.disabled = empty;
  }

  function watchRealButton() {
    var el = bar();
    var real = document.getElementById('checkout');
    if (!el || !real || typeof IntersectionObserver !== 'function') return;

    new IntersectionObserver(
      function (entries) {
        var visible = entries.some(function (entry) {
          return entry.isIntersecting;
        });
        el.classList.toggle('is-tucked', visible);
      },
      { threshold: 0.5 }
    ).observe(real);
  }

  function watchFooter() {
    var footer = document.getElementById('main-cart-footer');
    if (!footer || typeof MutationObserver !== 'function') return;

    // Dawn's cart.js swaps the footer's .js-contents after every quantity
    // change or removal and toggles .is-empty on the footer itself.
    new MutationObserver(function () {
      syncTotal();
      syncEmpty();
    }).observe(footer, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class'] });
  }

  function liftFloatingWidgets() {
    // The Shopify Forms "5% off" teaser is fixed to the bottom-left at the
    // maximum z-index inside an open shadow root, so page CSS cannot reach it
    // and it would sit on top of the bar's total. Lift it (and the expanded
    // form) above the bar. The Smile rewards launcher is lifted in the
    // section's CSS. The bar height is exposed as --nff-sticky-bar-h, which
    // inherits into the shadow root.
    var el = bar();
    if (el) root.style.setProperty('--nff-sticky-bar-h', el.offsetHeight + 'px');

    var tries = 0;
    (function attempt() {
      var hosts = document.querySelectorAll('shopify-forms-embed');
      var pending = hosts.length === 0;
      for (var i = 0; i < hosts.length; i++) {
        var shadow = hosts[i].shadowRoot;
        if (!shadow) {
          pending = true;
          continue;
        }
        if (shadow.querySelector('style[data-nff-sticky]')) continue;
        var style = document.createElement('style');
        style.setAttribute('data-nff-sticky', '');
        style.textContent =
          '.appEmbed[class*="appEmbedBottom"] {' +
          ' bottom: var(--nff-sticky-bar-h, 6.4rem) !important; }';
        shadow.appendChild(style);
      }
      if (pending && ++tries < 20) setTimeout(attempt, 500);
    })();
  }

  function renderStickyCheckOutBar() {
    if (enhanced) return;
    enhanced = true;

    function start() {
      syncTotal();
      syncEmpty();
      watchRealButton();
      watchFooter();
      liftFloatingWidgets();
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start);
    } else {
      start();
    }
  }

  function renderOriginal() {
    // The control is what the server already renders. Nothing to do.
  }

  /* ------------------------------------------------------------------ */
  /* Flag plumbing (same shape as the other noibu-flag-*.js files)       */
  /* ------------------------------------------------------------------ */

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);

    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }

    // NOIBUJS.addCustomAttribute comes from collect-core, which loads
    // independently of collect-ff, so reporting works even when flags do not.
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load. Is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (!root.classList.contains(className)) {
      root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
      root.classList.add(className);
      root.setAttribute('data-nff-sticky', variation);

      document.dispatchEvent(
        new CustomEvent('noibu:sticky-checkout-variation', { detail: { variation: variation } })
      );
    }

    if (variation === VARIATION_B) renderStickyCheckOutBar();
    else renderOriginal();
  }

  function remember(variation) {
    // Cached so a returning collector does not watch the bar appear after
    // load. The SDK stays the source of truth; this only feeds the paint-time
    // hint in the gate section.
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op: private mode or an embedded webview */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_sticky=sticky-check-out-bar (or =original)
    try {
      var value = new URLSearchParams(window.location.search).get('nff_sticky');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readVariation(client) {
    // See SDK NOTE in the header: read `variant`, then `variation`, then
    // `value`, then fall back to the control.
    if (typeof client.getStringDetails === 'function') {
      var details = client.getStringDetails(FLAG, CONTROL) || {};
      return details.variant || details.variation || details.value || CONTROL;
    }
    // Older SDK builds without getStringDetails.
    return client.getStringValue(FLAG, CONTROL);
  }

  function render(client) {
    var variation = readVariation(client);

    switch (variation) {
      case VARIATION_B:
        activate(VARIATION_B);
        remember(VARIATION_B);
        return VARIATION_B;
      case CONTROL:
      // falls through
      default:
        // Control, visitors excluded by targeting, and unresolved flags.
        activate(CONTROL);
        remember(CONTROL);
        return CONTROL;
    }
  }

  // The paint-time hint may already have applied a cached arm. Wire up the
  // bar's behaviour for it straight away; the SDK confirms or corrects it.
  if (root.getAttribute('data-nff-sticky') === VARIATION_B) renderStickyCheckOutBar();

  var forced = forcedVariation();
  if (forced) {
    // Skips remember() and the SDK so a forced preview neither poisons the
    // visitor's cache nor records an exposure.
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));

      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      // Do NOT remember() here: caching a value the SDK never produced would
      // replay a decision that was never made. A returning visitor keeps the
      // arm replayed from their earlier, real assignment (same as the cart,
      // PDP and search flags).
      if (!root.hasAttribute('data-nff-sticky')) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
